// Libarary
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// View Model
import { AccountVM } from '../../models/AccountMasterVM';
import { GrowerOrderData } from '../../models/GrowerOrderData';

// Service
import { ConfigService } from '../infrastructure/config.service';
import { BroadcastService } from '../infrastructure/broadcaster.service';
import { NetworkService } from '../infrastructure/network.service';

@Injectable()
export class GrowerOrderService {
  public SelectedAccountObj: BehaviorSubject<AccountVM>;
  public SelectedGrowerObj: BehaviorSubject<GrowerOrderData>;
  public isAccountSelectedOnLP = false;
  public isGrowerSelectedOnLP = false;
  public SelectedGwrOrderId: BehaviorSubject<string>;
  public selectedHybridProducts: any[] = []; // Property to track selected hybrid products

  constructor(public broadcastService: BroadcastService,
    public configService: ConfigService,
    private networkService: NetworkService) {
    this.SelectedAccountObj = new BehaviorSubject<AccountVM>(null);
    this.SelectedGrowerObj = new BehaviorSubject<GrowerOrderData>(null);
    this.SelectedGwrOrderId = new BehaviorSubject<string>('0');
  }

  public AccountChange(obj: AccountVM) {
    this.SelectedAccountObj.next(obj);
  }

  public GrowerChange(obj: any) {
    this.SelectedGrowerObj.next(obj);
  }

  public SetAccountState(selected: boolean) {
    this.isAccountSelectedOnLP = selected;
  }

  public SetGrowerState(selected: boolean) {
    this.isGrowerSelectedOnLP = selected;
  }

  // Get Account details for Account dropdown
  public getAccounts() {
    return this.networkService.getRequest(this.configService.getAccountListUrl());
  }

  // Get Account detail
  public getAccountInfo(cropYear, accountNumber) {
    return this.networkService.getRequest(this.configService.getAccountInfoUrl() + '?cropYear=' + cropYear + '&accountNumber=' + accountNumber);
  }

  // Get Seller List
  public getSellerList(accountNo) {
    return this.networkService.getRequest(this.configService.getSellerListUrl() + '?accountNo=' + accountNo);
  }

  // Get Grower order items
  public getGrowerOrderItems(growerOrderId, accountNo, cropYear) {
    return this.networkService.getRequest(this.configService.getGrowerOrderItemsUrl() + '?growerOrderId=' + growerOrderId + '&accountNo=' + accountNo + '&cropYear=' + cropYear);
  }

  // Add or update grower order
  public growerOrderAddUpdate(data: any) {
    return this.networkService.postRequest(this.configService.growerOrderAddUpdateUrl(), data);
  }

  // validateAccountForB2BEnablement
  public validateAccountForB2BEnablement(accountNo) {
    return this.networkService.getRequest(this.configService.validateAccountForB2BEnablement() + '?accountNo=' + accountNo);
  }

  // Get Grower Detail
  public getGrowerDetail(cropYear, shipToAccount, growerOrderId) {
    return this.networkService.getRequest(this.configService.getGrowerDetailUrl() + '?cropYear=' + cropYear + '&shipToAccount=' + shipToAccount + '&growerOrderId=' + growerOrderId);
  }

  // Get Product change
  public getGrowerProductChange(cropYear, itemId, action, accountNo) {
    return this.networkService.getRequest(this.configService.getGrowerProductChangeUrl() + '?cropYear=' + cropYear + '&itemId=' + itemId + '&action=' + action + '&accountNo=' + accountNo);
  }

  // Get Grower List
  public getGrowerList(cropYear, accountId, growerOrderId) {
    return this.networkService.getRequest(this.configService.getGrowerListUrl() + '?cropYear=' + cropYear + '&accountId=' + accountId + '&growerOrderId=' + growerOrderId);
  }

  // Get Product Availability
  public getProductAvailability(productFilters) {
    return this.networkService.postRequest(this.configService.getProductAvailabilityUrl(), productFilters);
  }

  // Get Grower order list
  public getGrowerAccount(cropYear, accountNo) {
    return this.networkService.getRequest(this.configService.getGrowerAccountUrl() + '?cropYear=' + cropYear + '&accountNo=' + accountNo);
  }

  // Get Grower list by account
  public getGrowerListByFilter(filters, growerFilters) {
    return this.networkService.postRequest(this.configService.getGrowerSearchUrl() + '?accountId=' + filters.shipToAccount + '&cropYear=' + filters.cropYear, growerFilters);
  }

  // Get Grower list by account
  public getOrderNumber() {
    return this.networkService.getRequest(this.configService.getItemNumberMasterUrl());
  }

  public getGrowerPDFdata(cropYear, growerOrderId) {
    return this.networkService.getRequest(this.configService.getGrowerPDFdataUrl() + '?cropYear=' + cropYear + '&growerOrderId=' + growerOrderId)
  }

  // pdf from base64 date
  public sendBaseDate(filters) {
    return this.networkService.postRequest(this.configService.postFileSeedPdf(), filters);
  }

  // Check Hybrid button access
  public hasAddHybridBtnAccess(accountNo) {
    return this.networkService.getRequest(this.configService.checkHyridBtnAccess() + '?accountNo=' + accountNo);
  }

  public getHybridVarietyPackageSizeList(accountNo) {
    return this.networkService.getRequest(this.configService.getGrowerHybridVarietyPackageSize() + '?accountNo=' + accountNo);
  }

  public setSelectedHybridProducts(products: any[]): void {
    this.selectedHybridProducts = products ? [...products] : [];
  }

  public removeSelectedHybridProducts(deletedItems: any[]): void {
    if (!deletedItems || deletedItems.length === 0) {
      return;
    }
    const deletedKeys = new Set(
      deletedItems.map(item => `${(item.brand || '').toLocaleUpperCase()}|${(item.packageSize || '').toLocaleUpperCase()}`)
    );
    this.selectedHybridProducts = this.selectedHybridProducts.filter(item => {
      const key = `${(item.brand || '').toLocaleUpperCase()}|${(item.packageSize || '').toLocaleUpperCase()}`;
      return !deletedKeys.has(key);
    });
  }

  public getSelectedHybridProducts(): any[] {
    return this.selectedHybridProducts;
  }

  public clearSelectedHybridProducts(): void {
    this.selectedHybridProducts = [];
  }

  public syncSelectedHybridProductsForOrder(currentOrderItems: any[]): void {
    const itemKey = (item: any) => `${(item.brand || '').toLocaleUpperCase()}|${(item.packageSize || '').toLocaleUpperCase()}`;
    const currentOrderKeys = new Set((currentOrderItems || []).map(itemKey));

    this.selectedHybridProducts = this.selectedHybridProducts.filter(item => currentOrderKeys.has(itemKey(item)));

    const trackedKeys = new Set(this.selectedHybridProducts.map(itemKey));
    (currentOrderItems || []).forEach(item => {
      const key = itemKey(item);
      if (!trackedKeys.has(key)) {
        this.selectedHybridProducts.push(item);
        trackedKeys.add(key);
      }
    });
  }
}
